/**
 * Block 13 PR-G Phase 4A foundation + Phase 4B per-region extension —
 * Default `ContractRequisite[]` rows builder.
 *
 * **Phase 4A** (committed 2026-05-23) ввёл минимальный KZ-набор 7 полей
 * для всех регионов. **Phase 4B** расширяет с учётом региональных шаблонов:
 *   - KZ minimal — 7 полей (ФИО / Адрес / ИИН-БИН / Телефон / ИИК / Банк / БИК)
 *   - RU — KZ subset + ИНН / КПП / ОГРН / ОГРНИП / Расчётный счёт / Корр. счёт (13)
 *   - UZ — KZ subset + ИНН / ПИНФЛ / Расчётный счёт / МФО (11)
 *
 * **Стабильность ID:** rows используют `${side}-${key}` template (`customer-iin_bin`,
 * `executor-bank` и т.д.). Это:
 *   1. Гарантирует уникальность React keys через customer+executor combined render
 *      (P-009 SectionRequisites рендерит обе стороны).
 *   2. Обеспечивает stable identity при перерендере (нет генерации UUID per render).
 *   3. ID соответствует Zod schema `.min(1).max(200)` constraint.
 *
 * Aibek approval 2026-05-23 decision #2: populate-on-creation
 * (customerRequisites + executorRequisites populated на creation с пустыми
 * values). Упрощает UI rendering — нет conditional empty-state.
 *
 * **Transient PII (P-009):** customer-side rows содержат placeholders для
 * чувствительных данных (ИИН/БИН/паспорт/адрес/телефон). Values clearable
 * post-PDF в Phase 7. Executor-side обычно собственные реквизиты пользователя
 * — не transient.
 *
 * Reference:
 *   - PRD §10.13.2 «Раздел "Реквизиты сторон"» (KZ minimal / RU extended / UZ extended)
 *   - prototype `docs/prototypes/screen-2.13.2-contract.html` REQ_FIELDS
 *   - ADR-0025 §Q2 (ContractRequisite schema)
 *   - `packages/shared/src/contracts/contracts.ts` CONTRACT_KNOWN_REQUISITE_KEYS
 */

import type { ContractRegion, ContractRequisite } from '@pomoshnik/shared';

import type { RequisiteSide } from './contractReducer';
// Re-export для consumers, который historically импортирует side type из этого
// модуля. Канонический источник истины — `contractReducer.ts` (где RequisiteSide
// используется в action types). Re-export сохраняет API compatibility.
export type { RequisiteSide };

interface RequisiteFieldSpec {
  key: string;
  label: string;
}

/**
 * Базовый KZ-набор (7 полей по таблице 1 шаблона KZ). RU/UZ расширяют через
 * concat — KZ subset идёт первым, чтобы основные поля шли по тому же порядку
 * во всех регионах (стабильный UX для пользователя, переключающего между
 * регионами через админ-панель в Phase 9+).
 */
const KZ_BASE_FIELDS: readonly RequisiteFieldSpec[] = [
  { key: 'name', label: 'ФИО / Наименование' },
  { key: 'address', label: 'Адрес' },
  { key: 'iin_bin', label: 'ИИН / БИН' },
  { key: 'phone', label: 'Телефон' },
  { key: 'iik', label: 'ИИК (Расчётный счёт)' },
  { key: 'bank', label: 'Банк' },
  { key: 'bik', label: 'БИК' },
];

/** RU-additional fields поверх KZ subset. */
const RU_EXTRA_FIELDS: readonly RequisiteFieldSpec[] = [
  { key: 'inn', label: 'ИНН' },
  { key: 'kpp', label: 'КПП' },
  { key: 'ogrn', label: 'ОГРН' },
  { key: 'ogrnip', label: 'ОГРНИП' },
  { key: 'r_s', label: 'Расчётный счёт' },
  { key: 'k_s', label: 'Корр. счёт' },
];

/** UZ-additional fields поверх KZ subset. */
const UZ_EXTRA_FIELDS: readonly RequisiteFieldSpec[] = [
  { key: 'inn', label: 'ИНН' },
  { key: 'pinfl', label: 'ПИНФЛ' },
  { key: 'r_s', label: 'Расчётный счёт' },
  { key: 'mfo', label: 'МФО' },
];

function fieldsForRegion(region: ContractRegion): readonly RequisiteFieldSpec[] {
  switch (region) {
    case 'kz':
      return KZ_BASE_FIELDS;
    case 'ru':
      // KZ base subset с переименованием iik→iik (название счёта остаётся ИИК
      // в KZ, в RU это «Расчётный счёт» как отдельное поле). KZ subset
      // сохраняет «ИИК» для compatibility с уже введёнными данными если
      // user сменил регион через админ (Phase 9+) — но это edge case, и
      // user сам перепишет values. Для Phase 4B используем concat as-is.
      return [...KZ_BASE_FIELDS, ...RU_EXTRA_FIELDS];
    case 'uz':
      return [...KZ_BASE_FIELDS, ...UZ_EXTRA_FIELDS];
  }
}

/**
 * Возвращает array of default-сконструированных rows для указанного региона +
 * стороны. Все `value: ''` (user fills). `removable: false` — fixed rows;
 * custom rows добавляются через UI `ADD_REQUISITE_ROW` action с `removable: true`.
 *
 * @param region — ContractRegion ('kz' | 'ru' | 'uz'). Определяет набор полей.
 * @param side — 'customer' | 'executor'. Side влияет на ID prefix (для уникальности
 *   в DOM react keys при рендеринге обеих сторон в SectionRequisites).
 */
export function buildDefaultRequisites(
  region: ContractRegion,
  side: RequisiteSide,
): ContractRequisite[] {
  return fieldsForRegion(region).map((field) => ({
    id: `${side}-${field.key}`,
    key: field.key,
    label: field.label,
    value: '',
    removable: false,
  }));
}
